import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { CodeInputField } from '../CodeInputField';
import { useMagicCodeLoginStore } from '../../stores/magicCodeLoginStore';
import { useAuthStore } from '../../stores/authStore';

interface CodeStepProps {
  email: string;
  onSuccess: () => void;
  onBack: () => void;
  autoSubmit?: boolean;
}

const CodeStep: React.FC<CodeStepProps> = ({
  email,
  onSuccess,
  onBack,
  autoSubmit = false,
}) => {
  const {
    code,
    loading,
    error,
    codeAttempts,
    maxCodeAttempts,
    setCode,
    setError,
    clearError,
    incrementCodeAttempts,
    goToEmailStep,
    reset,
  } = useMagicCodeLoginStore();

  const { verifyMagicCode, requestMagicCode } = useAuthStore();

  const storageKey = `magic-code-resend-expiry-${email}`;

  const getStoredExpiry = () => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const remainingFromExpiry = (expiry: number) =>
    Math.max(0, Math.ceil((expiry - Date.now()) / 1000));

  const [resendTimer, setResendTimer] = useState(() =>
    remainingFromExpiry(getStoredExpiry())
  );
  const [resendSuccess, setResendSuccess] = useState(false);

  const setResendExpiry = (seconds: number) => {
    const expiry = Date.now() + seconds * 1000;
    sessionStorage.setItem(storageKey, String(expiry));
    setResendTimer(seconds);
  };

  const isTimerActive = resendTimer > 0;

  useEffect(() => {
    if (!isTimerActive) return;
    const interval = setInterval(() => {
      const remaining = remainingFromExpiry(getStoredExpiry());
      setResendTimer(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerActive, storageKey]);

  const handleCodeSubmit = async () => {
    try {
      clearError();

      if (code.length !== 6) {
        setError('Please enter the complete 6-character code');
        return;
      }

      const result = await verifyMagicCode(email, code);

      if (result.success) {
        reset();
        onSuccess();
      } else if (result.shouldShowError && result.error) {
        // Show API errors (invalid code, server errors) and network errors
        setError(result.error);

        // Only increment attempts for invalid/expired code errors
        if (
          result.error.toLowerCase().includes('invalid') ||
          result.error.toLowerCase().includes('expired')
        ) {
          incrementCodeAttempts();
        }

        // Clear code input for retry
        setCode('');

        if (codeAttempts + 1 >= maxCodeAttempts) {
          setError('Too many attempts. Please request a new code.');
        }
      }
    } catch (error) {
      console.error('Error in handleCodeSubmit:', error);
      setError('Connection error. Please check your network and try again.');
    }
  };

  const handleRequestNewCode = async () => {
    if (resendTimer > 0) return;

    try {
      clearError();
      setCode('');
      setResendSuccess(false);
      const result = await requestMagicCode(email);

      // Always set cooldown after API call — prevents spamming regardless of outcome
      setResendExpiry(30);

      // Only show network errors - API errors are handled silently for security
      if (result.shouldShowError && result.error) {
        setError(result.error);
      }
      // Show success flash for accessibility
      if (!result.shouldShowError) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 4000);
      }
      // Note: For security, we don't show errors for invalid emails
      // The user will always see the "code sent" step
    } catch (error) {
      console.error('Error in handleRequestNewCode:', error);
      setError('Connection error. Please check your network and try again.');
    }
  };

  const handleBackToEmail = () => {
    goToEmailStep();
    onBack();
  };

  // Auto-submit when code is pre-filled from URL
  useEffect(() => {
    if (autoSubmit && code.length === 6 && !loading) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleCodeSubmit();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, code]);

  return (
    <Box data-testid="magic-code-code-step">
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          data-testid="magic-code-error-alert"
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <CodeInputField
          value={code}
          onChange={setCode}
          onComplete={handleCodeSubmit}
          disabled={loading}
          error={!!error && error.includes('code')}
          autoFocus
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleCodeSubmit}
        disabled={loading || code.length !== 6}
        data-testid="magic-code-verify-button"
        sx={{
          py: 1.5,
          fontSize: '1.1rem',
          mb: 2,
          position: 'relative',
        }}
      >
        {loading ? (
          <CircularProgress
            size={24}
            color="inherit"
            data-testid="magic-code-verify-loading"
          />
        ) : (
          'Login'
        )}
      </Button>

      {/* Code step actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToEmail}
          disabled={loading}
          data-testid="magic-code-back-button"
          sx={{ color: 'text.secondary' }}
        >
          Back
        </Button>

        <Link
          component="button"
          variant="body2"
          onClick={handleRequestNewCode}
          disabled={loading || resendTimer > 0}
          data-testid="magic-code-resend-link"
          sx={{
            color:
              loading || resendTimer > 0 ? 'text.disabled' : 'primary.main',
            cursor: loading || resendTimer > 0 ? 'default' : 'pointer',
          }}
        >
          {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
        </Link>
      </Box>

      {resendSuccess && (
        <Typography
          role="status"
          aria-live="polite"
          variant="caption"
          data-testid="magic-code-resend-success"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1,
            color: 'success.main',
          }}
        >
          New code sent to {email}
        </Typography>
      )}

      {codeAttempts > 0 && (
        <Typography
          variant="caption"
          data-testid="magic-code-attempts"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 2,
            color: 'warning.main',
          }}
        >
          Attempts: {codeAttempts}/{maxCodeAttempts}
        </Typography>
      )}
    </Box>
  );
};

export default CodeStep;
